import { useState, useEffect, useCallback, useRef } from 'react';
import { createLogger } from "@/lib/logger";
const log = createLogger("twilio-conv");
import { Client as ConversationsClient, Conversation, Message, Participant } from '@twilio/conversations';
import { useTwilioConversationsToken } from './useTwilioConversationsToken';
import { supabase } from '@/integrations/supabase/client';

// ── Types matching useEncryptedChat interface ──────────────────────────────

export interface TwilioChatRoom {
  id: string;              // Conversation SID
  name: string | null;
  is_encrypted: boolean;   // Twilio E2EE
  room_type_v2: string;    // 'direct' | 'group'
  created_at: string;
  other_user?: {
    user_id: string;
    full_name: string | null;
    avatar_url: string | null;
    phone_number?: string | null;
  };
  last_message?: TwilioChatMessage;
  unread_count?: number;
}

export interface TwilioChatMessage {
  id: string;
  room_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  is_encrypted: boolean;
  message_type: string | null;
}

export interface UseTwilioConversationsReturn {
  rooms: TwilioChatRoom[];
  messages: TwilioChatMessage[];
  isLoading: boolean;
  error: string | null;
  currentRoom: TwilioChatRoom | null;
  sendMessage: (content: string, options?: { targetRoomId?: string }) => Promise<void>;
  createDirectRoom: (otherUserId: string) => Promise<TwilioChatRoom | null>;
  selectRoom: (roomId: string) => void;
  markAsRead: (roomId: string) => Promise<void>;
  isReady: boolean;
  client: ConversationsClient | null;
  hasMoreMessages: boolean;
  isLoadingMore: boolean;
  fetchOlderMessages: (roomId: string) => Promise<void>;
}

/**
 * Hook that wraps Twilio Conversations SDK, providing an interface
 * compatible with useEncryptedChat for transparent routing.
 */
export function useTwilioConversations(enabled = true): UseTwilioConversationsReturn {
  const { token, identity, isLoading: tokenLoading, error: tokenError, refetch: refetchToken } = useTwilioConversationsToken(enabled);

  const [client, setClient] = useState<ConversationsClient | null>(null);
  const [rooms, setRooms] = useState<TwilioChatRoom[]>([]);
  const [messages, setMessages] = useState<TwilioChatMessage[]>([]);
  const [currentRoom, setCurrentRoom] = useState<TwilioChatRoom | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [dbRoomsLoaded, setDbRoomsLoaded] = useState(false);

  const clientRef = useRef<ConversationsClient | null>(null);
  const currentConversationRef = useRef<Conversation | null>(null);
  const conversationMapRef = useRef<Map<string, Conversation>>(new Map());
  
  // Track rooms marked as read to protect from poll overwrites
  const markedReadRoomsRef = useRef<Set<string>>(new Set());
  const currentRoomRef = useRef<TwilioChatRoom | null>(null);
  currentRoomRef.current = currentRoom;

  // ── DB-backed room loading (instant, independent of SDK) ─────────────────
  useEffect(() => {
    if (!enabled || !identity) return;
    let cancelled = false;

    async function loadRoomsFromDB() {
      try {
        log.debug('[TwilioConv] Loading rooms from DB for', identity.slice(0, 8));
        const { data, error: rpcError } = await supabase.rpc('get_user_rooms', {
          p_user_id: identity,
        });

        if (cancelled) return;
        if (rpcError) {
          log.error('[TwilioConv] DB room load error:', rpcError);
          return;
        }

        if (data && data.length > 0) {
          const dbRooms: TwilioChatRoom[] = data.map((row: Record<string, unknown>) => ({
            id: row.room_id as string,
            name: row.room_name as string | null,
            is_encrypted: row.is_encrypted as boolean,
            room_type_v2: (row.room_type_v2 as string) || 'direct',
            created_at: row.room_created_at as string,
            other_user: row.other_user_id ? {
              user_id: row.other_user_id as string,
              full_name: row.other_user_name as string | null,
              avatar_url: row.other_user_avatar as string | null,
              phone_number: row.other_user_phone as string | null,
            } : undefined,
            last_message: row.last_message_content ? {
              id: `db-${row.room_id}`,
              room_id: row.room_id as string,
              sender_id: (row.last_message_sender_id as string) || '',
              content: row.last_message_content as string,
              created_at: row.last_message_at as string,
              is_encrypted: row.is_encrypted as boolean,
              message_type: row.last_message_type as string | null,
            } : undefined,
            unread_count: Number(row.unread_count) || 0,
          }));
          log.debug('[TwilioConv] Loaded', dbRooms.length, 'rooms from DB');
          setRooms(dbRooms);
        }
      } catch (err) {
        log.error('[TwilioConv] DB room load failed:', err);
      } finally {
        if (!cancelled) {
          setDbRoomsLoaded(true);
          // Don't keep loading state if SDK isn't going to init quickly
          setIsLoading(false);
        }
      }
    }

    loadRoomsFromDB();
    return () => { cancelled = true; };
  }, [enabled, identity]);

  // ── Client-ready promise for awaiting initialization ──────────────────
  const clientReadyResolveRef = useRef<((c: ConversationsClient) => void) | null>(null);
  const clientReadyRejectRef = useRef<((e: Error) => void) | null>(null);
  const clientReadyPromiseRef = useRef<Promise<ConversationsClient> | null>(null);

  // Create a fresh ready promise when enabled OR when token changes (re-init)
  useEffect(() => {
    if (!enabled || !token) return;
    // Only create a new promise if we don't already have a live client
    if (clientRef.current) return;
    clientReadyPromiseRef.current = new Promise<ConversationsClient>((resolve, reject) => {
      clientReadyResolveRef.current = resolve;
      clientReadyRejectRef.current = reject;
    });
    // Prevent unhandled rejection if nobody awaits this promise
    clientReadyPromiseRef.current.catch(() => {});
  }, [enabled, token]);

  // ── Initialize Twilio Client ─────────────────────────────────────────────
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 2;

  useEffect(() => {
    if (!enabled || !token) return;

    let cancelled = false;

    async function initClient(attempt = 0) {
      try {
        log.debug(`[TwilioConv] Initializing client (attempt ${attempt + 1})...`);
        log.debug('[TwilioConv] Token received, length:', token!.length);
        
        let twilioClient: ConversationsClient;
        try {
          twilioClient = new ConversationsClient(token!);
          log.debug('[TwilioConv] Constructor succeeded');
        } catch (constructorErr) {
          log.error('[TwilioConv] Constructor failed:', constructorErr);
          throw new Error('Failed to create Twilio client. The token may be invalid.');
        }

        twilioClient.on('connectionStateChanged', (state) => {
          log.debug(`[TwilioConv] Connection state: ${state}`);
          if (state === 'connected') {
            log.debug('[TwilioConv] ✅ Connected to Twilio');
          }
          if (state === 'denied') {
            log.warn('[TwilioConv] Token denied, refreshing...');
            refetchToken();
          }
        });

        twilioClient.on('connectionError', (err: unknown) => {
          const errObj = err as Record<string, unknown> | null | undefined;
          const isTerminal = errObj?.terminal === true;
          const msg = errObj?.message
            ?? (errObj?.body as Record<string, unknown>)?.message
            ?? (errObj?.terminal !== undefined ? `Terminal=${errObj.terminal}` : null)
            ?? (typeof err === 'string' ? err : null)
            ?? 'Unknown connection error';
          log.warn('[TwilioConv] Connection error:', msg, 'terminal:', isTerminal, err);
          
          if (isTerminal) {
            setError(`Chat connection failed: ${msg}`);
            setIsLoading(false);
          }
        });

        twilioClient.on('tokenAboutToExpire', () => {
          log.debug('[TwilioConv] Token about to expire, refreshing...');
          refetchToken();
        });

        twilioClient.on('tokenExpired', () => {
          log.warn('[TwilioConv] Token expired, refreshing...');
          refetchToken();
        });

        // Wait for initialization with a timeout
        log.debug('[TwilioConv] Waiting for initialized event...');
        await Promise.race([
          new Promise<void>((resolve, reject) => {
            twilioClient.on('initialized', () => {
              log.debug('[TwilioConv] ✅ Received initialized event');
              resolve();
            });
            twilioClient.on('initFailed', (e) => {
              const msg = e?.error?.message ?? e?.error?.toString?.() ?? 'Twilio SDK initialization failed';
              log.error('[TwilioConv] ❌ initFailed event:', msg, e);
              reject(new Error(msg));
            });
          }),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Twilio client initialization timed out after 30s')), 30000)
          ),
        ]);

        if (cancelled) {
          twilioClient.shutdown();
          return;
        }

        log.debug('[TwilioConv] Client initialized successfully');
        retryCountRef.current = 0; // Reset on success
        clientRef.current = twilioClient;
        setClient(twilioClient);
        setError(null);

        // Resolve the ready promise
        clientReadyResolveRef.current?.(twilioClient);

        // Load conversations
        log.debug('[TwilioConv] Loading conversations...');
        await loadConversations(twilioClient);
        log.debug('[TwilioConv] ✅ Conversations loaded');

        // Listen for new conversations
        twilioClient.on('conversationAdded', handleConversationAdded);
        twilioClient.on('conversationRemoved', handleConversationRemoved);

        setIsLoading(false);
      } catch (err) {
        if (cancelled) return;
        
        log.error(`[TwilioConv] Init failed (attempt ${attempt + 1}):`, err);
        const errMsg = err instanceof Error ? err.message : 'Failed to initialize Twilio';
        
        // Retry with token refresh
        if (attempt < MAX_RETRIES) {
          log.debug(`[TwilioConv] Retrying in 2s (${attempt + 1}/${MAX_RETRIES})...`);
          retryCountRef.current = attempt + 1;
          refetchToken();
          await new Promise(r => setTimeout(r, 2000));
          if (!cancelled) {
            return initClient(attempt + 1);
          }
        } else {
          setError(errMsg);
          setIsLoading(false);
          clientReadyRejectRef.current?.(new Error(errMsg));
        }
      }
    }

    initClient(retryCountRef.current > 0 ? retryCountRef.current : 0);

    return () => {
      cancelled = true;
      if (clientRef.current) {
        log.debug('[TwilioConv] Shutting down client');
        clientRef.current.shutdown();
        clientRef.current = null;
        setClient(null);
        // Invalidate ready promise so awaitClientReady creates a fresh one
        clientReadyPromiseRef.current = null;
        clientReadyResolveRef.current = null;
        clientReadyRejectRef.current = null;
      }
    };
  }, [enabled, token]);

  // ── Update token on refresh ──────────────────────────────────────────────
  useEffect(() => {
    if (client && token) {
      client.updateToken(token);
    }
  }, [token, client]);

  // ── Load all conversations ───────────────────────────────────────────────
  async function loadConversations(twilioClient: ConversationsClient) {
    try {
      const paginator = await twilioClient.getSubscribedConversations();
      const convRooms: TwilioChatRoom[] = [];

      for (const conv of paginator.items) {
        conversationMapRef.current.set(conv.sid, conv);
        const room = await conversationToRoom(conv);
        convRooms.push(room);

        // Listen for messages on each conversation
        conv.on('messageAdded', (msg) => handleMessageAdded(conv.sid, msg));
      }

      // Sort by last message time
      convRooms.sort((a, b) => {
        const aTime = a.last_message?.created_at || a.created_at;
        const bTime = b.last_message?.created_at || b.created_at;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });

      setRooms(convRooms);
    } catch (err) {
      log.error('[TwilioConv] Failed to load conversations:', err);
    }
  }

  // ── Convert Twilio Conversation → ChatRoom ───────────────────────────────
  async function conversationToRoom(conv: Conversation): Promise<TwilioChatRoom> {
    let lastMsg: TwilioChatMessage | undefined;
    let unreadCount = 0;

    try {
      const msgPaginator = await conv.getMessages(1);
      if (msgPaginator.items.length > 0) {
        lastMsg = twilioMessageToChat(conv.sid, msgPaginator.items[0]);
      }
      const count = await conv.getUnreadMessagesCount();
      unreadCount = count ?? 0;
    } catch {
      // Conversation may not have messages yet
    }

    // Determine other participant for direct conversations
    let otherUser: TwilioChatRoom['other_user'];
    const participants = await conv.getParticipants();
    const otherParticipant = participants.find((p) => p.identity !== identity);

    if (otherParticipant?.identity) {
      // Look up profile from our DB
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url, phone_number')
        .eq('user_id', otherParticipant.identity)
        .single();

      if (profile) {
        otherUser = {
          user_id: profile.user_id,
          full_name: profile.full_name,
          avatar_url: profile.avatar_url,
          phone_number: profile.phone_number,
        };
      }
    }

    return {
      id: conv.sid,
      name: conv.friendlyName ?? null,
      is_encrypted: true, // Twilio E2EE enabled
      room_type_v2: participants.length <= 2 ? 'direct' : 'group',
      created_at: conv.dateCreated?.toISOString() ?? new Date().toISOString(),
      other_user: otherUser,
      last_message: lastMsg,
      unread_count: unreadCount,
    };
  }

  // ── Convert Twilio Message → ChatMessage ─────────────────────────────────
  function twilioMessageToChat(roomId: string, msg: Message): TwilioChatMessage {
    return {
      id: msg.sid,
      room_id: roomId,
      sender_id: msg.author ?? '',
      content: msg.body ?? '',
      created_at: msg.dateCreated?.toISOString() ?? new Date().toISOString(),
      is_encrypted: true,
      message_type: msg.type === 'media' ? 'file' : 'text',
    };
  }

  // ── Event Handlers ───────────────────────────────────────────────────────
  function handleConversationAdded(conv: Conversation) {
    conversationMapRef.current.set(conv.sid, conv);
    conv.on('messageAdded', (msg) => handleMessageAdded(conv.sid, msg));

    conversationToRoom(conv).then((room) => {
      setRooms((prev) => [room, ...prev]);
    });
  }

  function handleConversationRemoved(conv: Conversation) {
    conversationMapRef.current.delete(conv.sid);
    setRooms((prev) => prev.filter((r) => r.id !== conv.sid));
  }

  function handleMessageAdded(roomId: string, msg: Message) {
    const chatMsg = twilioMessageToChat(roomId, msg);
    const isActiveRoom = currentRoomRef.current?.id === roomId;

    // Update messages if viewing this room
    if (isActiveRoom) {
      setMessages((prev) => [...prev, chatMsg]);
    }

    // Update room's last message and unread count
    setRooms((prev) =>
      prev.map((room) => {
        if (room.id !== roomId) return room;
        return {
          ...room,
          last_message: chatMsg,
          unread_count: isActiveRoom ? 0 : (room.unread_count ?? 0) + 1,
        };
      }).sort((a, b) => {
        const aTime = a.last_message?.created_at || a.created_at;
        const bTime = b.last_message?.created_at || b.created_at;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      }),
    );
  }

  // ── Await client readiness with timeout ──────────────────────────────────
  async function awaitClientReady(timeoutMs = 15000): Promise<ConversationsClient> {
    // If we already have a live client, return it immediately
    if (clientRef.current) {
      log.debug('[TwilioConv] awaitClientReady: client already available');
      return clientRef.current;
    }
    if (!clientReadyPromiseRef.current) {
      log.error('[TwilioConv] awaitClientReady: no ready promise available');
      throw new Error('Chat service is not enabled.');
    }

    log.debug('[TwilioConv] awaitClientReady: waiting for client initialization...');
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Chat service is taking too long to connect. Please check your network and try again.')), timeoutMs),
    );

    return Promise.race([clientReadyPromiseRef.current, timeoutPromise]);
  }

  // ── DB-level mark as read (shared helper) ────────────────────────────────
  const markAsReadDb = useCallback(async (roomId: string) => {
    if (!identity) return;
    markedReadRoomsRef.current.add(roomId);
    setRooms((prev) =>
      prev.map((r) => (r.id === roomId ? { ...r, unread_count: 0 } : r)),
    );
    const now = new Date().toISOString();
    await supabase
      .from('chat_room_members')
      .update({ last_read_at: now })
      .eq('room_id', roomId)
      .eq('user_id', identity);
    setTimeout(() => {
      markedReadRoomsRef.current.delete(roomId);
    }, 60000);
  }, [identity]);

  // ── Select Room ──────────────────────────────────────────────────────────
  const selectRoom = useCallback(async (roomId: string) => {
    const conv = conversationMapRef.current.get(roomId);
    const room = rooms.find((r) => r.id === roomId) ?? null;
    setCurrentRoom(room);

    if (!conv) {
      // Room loaded from DB but SDK not ready - load messages from DB
      log.debug('[TwilioConv] No SDK conversation for room, loading messages from DB');
      try {
        const { data: dbMessages } = await supabase
          .from('chat_messages')
          .select('id, room_id, sender_id, content, created_at, is_encrypted, message_type')
          .eq('room_id', roomId)
          .order('created_at', { ascending: true })
          .limit(50);

        if (dbMessages) {
          setMessages(dbMessages.map((m) => ({
            id: m.id,
            room_id: m.room_id,
            sender_id: m.sender_id,
            content: m.is_encrypted ? '🔒 Encrypted message' : m.content,
            created_at: m.created_at ?? new Date().toISOString(),
            is_encrypted: m.is_encrypted,
            message_type: m.message_type,
          })));
        }
      } catch (err) {
        log.error('[TwilioConv] Failed to load messages from DB:', err);
      }
      markAsReadDb(roomId);
      return;
    }

    currentConversationRef.current = conv;

    // Load messages via SDK
    try {
      const paginator = await conv.getMessages(50);
      const chatMessages = paginator.items.map((m) => twilioMessageToChat(roomId, m));
      setMessages(chatMessages);
      setHasMoreMessages(paginator.hasPrevPage);

      // Mark as read via SDK + DB
      try { await conv.setAllMessagesRead(); } catch { /* non-fatal */ }
      markAsReadDb(roomId);
    } catch (err) {
      log.error('[TwilioConv] Failed to load messages:', err);
    }
  }, [rooms, markAsReadDb]);

  // ── Supabase Realtime subscription for live message updates ──────────────
  // currentRoomRef declared above handles this

  useEffect(() => {
    if (!enabled || !currentRoom?.id || !identity) return;

    const roomId = currentRoom.id;
    log.debug('[TwilioConv] Subscribing to realtime for room:', roomId.slice(0, 8));

    const channel = supabase
      .channel(`chat-rt:${roomId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `room_id=eq.${roomId}`,
        },
        (payload) => {
          const newRow = payload.new as Record<string, unknown>;
          // Skip own messages already added optimistically
          if (newRow.sender_id === identity && (newRow.id as string)?.startsWith?.('local-')) return;

          const msg: TwilioChatMessage = {
            id: newRow.id as string,
            room_id: newRow.room_id as string,
            sender_id: newRow.sender_id as string,
            content: (newRow.is_encrypted ? '🔒 Encrypted message' : newRow.content) as string,
            created_at: (newRow.created_at as string) ?? new Date().toISOString(),
            is_encrypted: newRow.is_encrypted as boolean,
            message_type: newRow.message_type as string | null,
          };

          // Deduplicate: skip if already in state
          setMessages((prev) => {
            if (prev.some((m) => m.id === msg.id)) return prev;
            // Replace optimistic local message from same sender with same content
            const withoutOptimistic = prev.filter(
              (m) => !(m.id.startsWith('local-') && m.sender_id === msg.sender_id && m.content === msg.content)
            );
            return [...withoutOptimistic, msg];
          });

          // Update room last_message
          setRooms((prev) =>
            prev.map((r) =>
              r.id === roomId ? { ...r, last_message: msg, unread_count: currentRoomRef.current?.id === roomId ? 0 : (r.unread_count ?? 0) + 1 } : r
            ).sort((a, b) => {
              const aTime = a.last_message?.created_at || a.created_at;
              const bTime = b.last_message?.created_at || b.created_at;
              return new Date(bTime).getTime() - new Date(aTime).getTime();
            }),
          );
        }
      )
      .subscribe();

    return () => {
      log.debug('[TwilioConv] Unsubscribing realtime for room:', roomId.slice(0, 8));
      supabase.removeChannel(channel);
    };
  }, [enabled, currentRoom?.id, identity]);

  // ── Periodic room list refresh (fallback for chat list updates) ──────────
  useEffect(() => {
    if (!enabled || !identity) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await supabase.rpc('get_user_rooms', { p_user_id: identity });
        if (data && data.length > 0) {
          const activeRoomId = currentRoomRef.current?.id;
          const protectedRooms = markedReadRoomsRef.current;
          
          const dbRooms: TwilioChatRoom[] = data.map((row: Record<string, unknown>) => {
            const roomId = row.room_id as string;
            // Protect active room and recently-read rooms from stale unread counts
            const isProtected = roomId === activeRoomId || protectedRooms.has(roomId);
            
            return {
              id: roomId,
              name: row.room_name as string | null,
              is_encrypted: row.is_encrypted as boolean,
              room_type_v2: (row.room_type_v2 as string) || 'direct',
              created_at: row.room_created_at as string,
              other_user: row.other_user_id ? {
                user_id: row.other_user_id as string,
                full_name: row.other_user_name as string | null,
                avatar_url: row.other_user_avatar as string | null,
                phone_number: row.other_user_phone as string | null,
              } : undefined,
              last_message: row.last_message_content ? {
                id: `db-${roomId}`,
                room_id: roomId,
                sender_id: (row.last_message_sender_id as string) || '',
                content: row.last_message_content as string,
                created_at: row.last_message_at as string,
                is_encrypted: row.is_encrypted as boolean,
                message_type: row.last_message_type as string | null,
              } : undefined,
              unread_count: isProtected ? 0 : (Number(row.unread_count) || 0),
            };
          });
          setRooms(dbRooms);
        }
      } catch { /* silent */ }
    }, 10_000);

    return () => clearInterval(interval);
  }, [enabled, identity]);

  // ── Send Message ─────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (content: string, options?: { targetRoomId?: string; messageType?: string }) => {
    const targetId = options?.targetRoomId ?? currentConversationRef.current?.sid ?? currentRoom?.id;
    if (!targetId || !identity) {
      log.error('[TwilioConv] No target conversation or identity');
      return;
    }

    // Try SDK first
    const conv = conversationMapRef.current.get(targetId);
    if (conv) {
      try {
        await conv.sendMessage(content);
        return;
      } catch (err) {
        log.error('[TwilioConv] SDK sendMessage failed, falling back to DB:', err);
      }
    }

    // Fallback: insert directly into chat_messages table
    log.debug('[TwilioConv] Sending message via DB fallback for room:', targetId);
    const { error: insertErr } = await supabase
      .from('chat_messages')
      .insert({
        room_id: targetId,
        sender_id: identity,
        content,
        is_encrypted: false,
        message_type: options?.messageType || 'text',
        metadata: { sent_via: 'TWILIO_CONVERSATIONS' },
      });

    if (insertErr) {
      log.error('[TwilioConv] DB message insert failed:', insertErr);
      throw new Error('Failed to send message. Please try again.');
    }

    // Add to local state immediately for instant feedback
    const newMsg: TwilioChatMessage = {
      id: `local-${Date.now()}`,
      room_id: targetId,
      sender_id: identity,
      content,
      created_at: new Date().toISOString(),
      is_encrypted: false,
      message_type: options?.messageType || 'text',
    };
    setMessages((prev) => [...prev, newMsg]);

    // Update room's last message
    setRooms((prev) =>
      prev.map((room) =>
        room.id === targetId ? { ...room, last_message: newMsg } : room
      ).sort((a, b) => {
        const aTime = a.last_message?.created_at || a.created_at;
        const bTime = b.last_message?.created_at || b.created_at;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      }),
    );
  }, [identity, currentRoom]);

  // ── Ensure DB room + members exist (for authorization checks) ─────────
  async function ensureDbRoomExists(userA: string, userB: string) {
    // Check if a shared room already exists in DB
    const { data: myRooms } = await supabase
      .from('chat_room_members')
      .select('room_id')
      .eq('user_id', userA);

    if (myRooms && myRooms.length > 0) {
      const roomIds = myRooms.map(r => r.room_id);
      const { data: shared } = await supabase
        .from('chat_room_members')
        .select('room_id')
        .eq('user_id', userB)
        .in('room_id', roomIds)
        .limit(1)
        .maybeSingle();
      if (shared) {
        log.debug('[TwilioConv] DB room already exists:', shared.room_id);
        return;
      }
    }

    // Create room + members via edge function (service role needed)
    log.debug('[TwilioConv] Creating DB room for authorization...');
    await supabase.functions.invoke('twilio-create-conversation', {
      body: { otherUserId: userB },
    });
  }

  // ── Create Direct Room (tries SDK, falls back to edge function) ──────────
  const createDirectRoom = useCallback(async (otherUserId: string): Promise<TwilioChatRoom | null> => {
    log.debug('[TwilioConv] createDirectRoom invoked for', otherUserId.slice(0, 8));

    // Check if conversation already exists locally
    const existing = rooms.find(
      (r) => r.room_type_v2 === 'direct' && r.other_user?.user_id === otherUserId,
    );
    if (existing) {
      await selectRoom(existing.id);
      return existing;
    }

    const withTimeout = <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> =>
      Promise.race([
        promise,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(`${label} timed out after ${ms / 1000}s`)), ms),
        ),
      ]);

    // Try SDK if client is already live
    if (clientRef.current) {
      try {
        const twilioClient = clientRef.current;
        const uniqueName = `direct_${[identity, otherUserId].sort().join('_')}`;

        log.debug('[TwilioConv] Provisioning target user:', otherUserId.slice(0, 8));
        try {
          await withTimeout(supabase.functions.invoke('provision-twilio-user', { body: { targetUserId: otherUserId } }), 15_000, 'provision-twilio-user');
        } catch (provErr) {
          log.warn('[TwilioConv] Provisioning failed (non-fatal):', provErr);
        }

        let conv: Conversation;
        try {
          conv = await withTimeout(twilioClient.getConversationByUniqueName(uniqueName), 15_000, 'getByUniqueName');
        } catch {
          conv = await withTimeout(twilioClient.createConversation({ friendlyName: null, uniqueName }), 15_000, 'createConv');
        }
        try { await withTimeout(conv.join(), 15_000, 'conv.join'); } catch { /* already joined */ }
        try {
          await withTimeout(conv.add(otherUserId), 15_000, 'conv.add');
        } catch (addErr: unknown) {
          const m = addErr instanceof Error ? addErr.message : String(addErr);
          if (!m.includes('50433') && !m.includes('already')) log.error('[TwilioConv] Add participant error:', m);
        }

        const room = await conversationToRoom(conv);
        conversationMapRef.current.set(conv.sid, conv);
        conv.on('messageAdded', (msg) => handleMessageAdded(conv.sid, msg));
        setRooms((prev) => [room, ...prev.filter((r) => r.id !== conv.sid)]);
        currentConversationRef.current = conv;
        setCurrentRoom(room);
        setMessages([]);

        // Ensure DB records exist for authorization (can_call_user → shares_chat_room)
        ensureDbRoomExists(identity!, otherUserId).catch((err) =>
          log.warn('[TwilioConv] DB room sync failed (non-fatal):', err)
        );

        log.debug('[TwilioConv] createDirectRoom (SDK) finished:', room.id);
        return room;
      } catch (sdkErr) {
        log.warn('[TwilioConv] SDK createDirectRoom failed, falling back:', sdkErr);
      }
    } else {
      log.debug('[TwilioConv] SDK not ready, using edge function fallback');
    }

    // Fallback: create room via edge function
    try {
      await supabase.functions.invoke('provision-twilio-user', { body: { targetUserId: otherUserId } });

      const { data, error: fnError } = await supabase.functions.invoke('twilio-create-conversation', {
        body: { otherUserId },
      });
      if (fnError) throw new Error('Failed to start conversation.');
      if (!data?.success) throw new Error(data?.error || 'Failed to create conversation');

      const { data: profile } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url, phone_number')
        .eq('user_id', otherUserId)
        .single();

      const room: TwilioChatRoom = {
        id: data.conversationSid,
        name: null,
        is_encrypted: true,
        room_type_v2: 'direct',
        created_at: new Date().toISOString(),
        other_user: profile ? {
          user_id: profile.user_id,
          full_name: profile.full_name,
          avatar_url: profile.avatar_url,
          phone_number: profile.phone_number,
        } : undefined,
        unread_count: 0,
      };

      setRooms((prev) => [room, ...prev]);
      setCurrentRoom(room);
      setMessages([]);
      log.debug('[TwilioConv] createDirectRoom (edge fn) finished:', room.id);
      return room;
    } catch (err) {
      log.error('[TwilioConv] Edge fn createDirectRoom failed:', err);
      throw new Error('Failed to start conversation. Please try again.');
    }
  }, [rooms, identity, selectRoom]);

  // ── Mark as Read (public API - delegates to markAsReadDb + SDK) ──────────
  const markAsRead = useCallback(async (roomId: string) => {
    // Try SDK if available
    const conv = conversationMapRef.current.get(roomId);
    if (conv) {
      try { await conv.setAllMessagesRead(); } catch { /* non-fatal */ }
    }
    // Always update DB
    await markAsReadDb(roomId);
  }, [markAsReadDb]);

  // ── Fetch Older Messages ─────────────────────────────────────────────────
  const fetchOlderMessages = useCallback(async (roomId: string) => {
    const conv = conversationMapRef.current.get(roomId);
    if (!conv || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      // Get messages before the oldest current message
      const oldestIdx = messages.length > 0 ? 0 : undefined;
      const paginator = await conv.getMessages(30, oldestIdx);
      const olderMsgs = paginator.items.map((m) => twilioMessageToChat(roomId, m));
      setMessages((prev) => [...olderMsgs, ...prev]);
      setHasMoreMessages(paginator.hasPrevPage);
    } catch (err) {
      log.error('[TwilioConv] Failed to load older messages:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [messages, isLoadingMore]);

  return {
    rooms,
    messages,
    isLoading: !dbRoomsLoaded && (isLoading || tokenLoading),
    error: error ?? (tokenError?.message ?? null),
    currentRoom,
    sendMessage,
    createDirectRoom,
    selectRoom,
    markAsRead,
    isReady: dbRoomsLoaded || (!!client && !isLoading),
    client,
    hasMoreMessages,
    isLoadingMore,
    fetchOlderMessages,
  };
}
